<template>
    <div class="min-h-screen bg-gray-50">
        <AppHeader />

        <!-- Hero Section -->
        <section
            class="relative overflow-hidden bg-gradient-to-br from-primary-50 to-secondary-50 section-padding pt-32">
            <!-- Background Pattern -->
            <div class="absolute inset-0 opacity-10">
                <div class="absolute top-0 right-0 w-96 h-96 rounded-full bg-white blur-3xl"></div>
                <div class="absolute bottom-0 left-0 w-80 h-80 rounded-full bg-secondary-400 blur-3xl"></div>
            </div>

            <div class="container-custom relative z-10">
                <div class="text-center max-w-3xl mx-auto">
                    <div
                        class="inline-flex items-center px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-black text-sm font-medium mb-6">
                        <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z">
                            </path>
                        </svg>
                        Our Products
                    </div>
                    <h1 class="font-heading text-4xl lg:text-5xl font-bold text-black mb-6">
                        Discover Amazing Products
                    </h1>
                    <p class="text-xl text-black/80 mb-8">
                        Explore our collection of powerful mobile apps, web applications, and desktop software designed
                        to make your life easier.
                    </p>

                    <!-- Search Bar -->
                    <div class="max-w-xl mx-auto">
                        <div class="relative">
                            <input v-model="searchQuery" type="text" placeholder="Search products..."
                                class="w-full px-6 py-4 pr-14 rounded-2xl bg-white/95 backdrop-blur-sm text-gray-800 placeholder-gray-500 focus:outline-none focus:ring-4 focus:ring-white/30 shadow-2xl transition-all"
                                @input="handleSearch" />
                            <button
                                class="absolute right-3 top-1/2 -translate-y-1/2 p-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 transition-colors">
                                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <!-- Category Pills -->
        <section class="bg-white border-b border-gray-100 sticky top-16 z-40">
            <div class="container-custom py-4">
                <div class="flex items-center gap-3 overflow-x-auto scrollbar-hide">
                    <button @click="selectedCategory = null" :class="[
                        'flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200',
                        selectedCategory === null
                            ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    ]">
                        All Products
                    </button>
                    <button v-for="category in categories" :key="category.id" @click="selectedCategory = category.id"
                        :class="[
                            'flex-shrink-0 px-5 py-2.5 rounded-full text-sm font-semibold transition-all duration-200 whitespace-nowrap',
                            selectedCategory === category.id
                                ? 'bg-primary-600 text-white shadow-lg shadow-primary-500/30'
                                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        ]">
                        {{ category.name }}
                    </button>
                </div>
            </div>
        </section>

        <!-- Featured Products Carousel -->
        <section v-if="featuredProducts.length > 0 && !searchQuery" class="section-padding bg-white">
            <div class="container-custom">
                <div class="flex items-center justify-between mb-8">
                    <div>
                        <h2 class="font-heading text-2xl lg:text-3xl font-bold text-gray-900">
                            Featured Products
                        </h2>
                        <p class="text-gray-600 mt-1">Handpicked products for you</p>
                    </div>
                </div>

                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <router-link v-for="product in featuredProducts" :key="product.id"
                        :to="{ name: 'ProductDetail', params: { id: product.uid } }"
                        class="group relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary-500 to-secondary-500 p-1">
                        <div class="bg-white rounded-[22px] overflow-hidden h-full">
                            <!-- Banner -->
                            <div
                                class="relative h-40 bg-gradient-to-br from-primary-100 to-secondary-100 overflow-hidden">
                                <img v-if="product.banner_image_url" :src="product.banner_image_url"
                                    :alt="product.title"
                                    class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div class="absolute top-3 right-3">
                                    <span
                                        class="px-3 py-1 bg-yellow-400 text-yellow-900 text-xs font-bold rounded-full flex items-center gap-1">
                                        <svg class="w-3 h-3 fill-current" viewBox="0 0 20 20">
                                            <path
                                                d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                        </svg>
                                        Featured
                                    </span>
                                </div>
                            </div>

                            <!-- Content -->
                            <div class="p-5">
                                <div class="flex items-start gap-4">
                                    <!-- Icon -->
                                    <div
                                        class="flex-shrink-0 w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-100 to-secondary-100 p-0.5 shadow-lg -mt-10 relative z-10">
                                        <div class="w-full h-full rounded-[14px] overflow-hidden bg-white">
                                            <img v-if="product.icon_image_url" :src="product.icon_image_url"
                                                :alt="product.title" class="w-full h-full object-cover" />
                                            <div v-else
                                                class="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center">
                                                <span class="text-2xl font-bold text-black">{{ product.title?.charAt(0)
                                                    }}</span>
                                            </div>
                                        </div>
                                    </div>

                                    <!-- Info -->
                                    <div class="flex-1 min-w-0">
                                        <h3
                                            class="text-lg font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                                            {{ product.title }}
                                        </h3>
                                        <p class="text-sm text-gray-500 mt-0.5">{{ product.developer_name || 'Developer'
                                            }}</p>
                                        <div class="flex items-center gap-2 mt-2">
                                            <div class="flex items-center text-yellow-400">
                                                <svg v-for="i in 5" :key="i" class="w-4 h-4 fill-current"
                                                    :class="i <= Math.round(product.rating || 0) ? 'text-yellow-400' : 'text-gray-300'"
                                                    viewBox="0 0 20 20">
                                                    <path
                                                        d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                                </svg>
                                            </div>
                                            <span class="text-sm text-gray-600">{{ product.rating || '0' }}</span>
                                            <span class="text-gray-300">•</span>
                                            <span class="text-sm text-gray-600">({{ (parseInt(product.rating_count) || 0) + (product.testimonials_count || 0) }} Say)</span>
                                            <span class="text-gray-300">•</span>
                                            <span class="text-sm text-gray-600 font-medium">{{ formatUsers(product.total_users) }}
                                                users</span>
                                        </div>
                                    </div>
                                </div>

                                <p class="text-sm text-gray-600 mt-4 line-clamp-2">
                                    {{ product.short_description }}
                                </p>

                                <div class="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                                    <span :class="[
                                        'px-3 py-1 rounded-full text-xs font-semibold',
                                        getPlatformClass(product.platform)
                                    ]">
                                        {{ formatPlatform(product.platform) }}
                                    </span>
                                    <span class="text-lg font-bold"
                                        :class="product.price == 0 ? 'text-green-600' : 'text-gray-900'">
                                        {{ product.price == 0 ? 'Free' : '$' + product.price }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </router-link>
                </div>
            </div>
        </section>

        <!-- All Products Grid -->
        <section class="section-padding"
            :class="featuredProducts.length > 0 && !searchQuery ? 'bg-gray-50' : 'bg-white'">
            <div class="container-custom">
                <div class="flex items-center justify-between mb-8">
                    <div>
                        <h2 class="font-heading text-2xl lg:text-3xl font-bold text-gray-900">
                            {{ searchQuery ? 'Search Results' : 'All Products' }}
                        </h2>
                        <p class="text-gray-600 mt-1">
                            {{ filteredProducts.length }} product{{ filteredProducts.length !== 1 ? 's' : '' }}
                            available
                        </p>
                    </div>

                    <!-- Sort Dropdown -->
                    <select v-model="sortBy"
                        class="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500">
                        <option value="newest">Newest First</option>
                        <option value="popular">Most Popular</option>
                        <option value="rating">Highest Rated</option>
                        <option value="price_low">Price: Low to High</option>
                        <option value="price_high">Price: High to Low</option>
                    </select>
                </div>

                <!-- Loading State -->
                <div v-if="loading" class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <div v-for="i in 8" :key="i" class="bg-white rounded-2xl p-4 shadow-sm animate-pulse">
                        <div class="h-32 bg-gray-200 rounded-xl mb-4"></div>
                        <div class="flex items-start gap-3">
                            <div class="w-12 h-12 bg-gray-200 rounded-xl"></div>
                            <div class="flex-1">
                                <div class="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                                <div class="h-3 bg-gray-200 rounded w-1/2"></div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Products Grid -->
                <div v-else-if="filteredProducts.length > 0"
                    class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    <router-link v-for="product in filteredProducts" :key="product.id"
                        :to="{ name: 'ProductDetail', params: { id: product.uid } }"
                        class="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <!-- Product Card -->
                        <div class="relative h-32 bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden">
                            <img v-if="product.banner_image_url" :src="product.banner_image_url" :alt="product.title"
                                class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                            <div v-else class="w-full h-full flex items-center justify-center">
                                <svg class="w-12 h-12 text-gray-300" fill="none" stroke="currentColor"
                                    viewBox="0 0 24 24">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                        d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z">
                                    </path>
                                </svg>
                            </div>

                            <!-- Price Badge -->
                            <div class="absolute top-3 right-3">
                                <span :class="[
                                    'px-2 py-1 rounded-lg text-xs font-bold backdrop-blur-sm',
                                    product.price == 0 ? 'bg-green-500/90 text-black' : 'bg-white/90 text-gray-800'
                                ]">
                                    {{ product.price == 0 ? 'Free' : '$' + product.price }}
                                </span>
                            </div>
                        </div>

                        <div class="p-4">
                            <div class="flex items-start gap-3">
                                <!-- Icon -->
                                <div
                                    class="flex-shrink-0 w-12 h-12 rounded-xl overflow-hidden bg-gradient-to-br from-primary-100 to-secondary-100 shadow-sm -mt-8 relative z-10 border-2 border-white">
                                    <img v-if="product.icon_image_url" :src="product.icon_image_url"
                                        :alt="product.title" class="w-full h-full object-cover" />
                                    <div v-else
                                        class="w-full h-full bg-gradient-to-br from-primary-400 to-secondary-400 flex items-center justify-center">
                                        <span class="text-lg font-bold text-black">{{ product.title?.charAt(0) }}</span>
                                    </div>
                                </div>

                                <!-- Info -->
                                <div class="flex-1 min-w-0 pt-1">
                                    <h3
                                        class="text-sm font-bold text-gray-900 truncate group-hover:text-primary-600 transition-colors">
                                        {{ product.title }}
                                    </h3>
                                    <p class="text-xs text-gray-500 truncate mt-0.5">{{ product.developer_name ||
                                        'Developer' }}</p>
                                </div>
                            </div>

                            <div class="mt-3 flex items-center justify-between">
                                <div class="flex items-center gap-1">
                                    <svg class="w-4 h-4 text-yellow-400 fill-current" viewBox="0 0 20 20">
                                        <path
                                            d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                                    </svg>
                                    <span class="text-sm font-medium text-gray-700">{{ product.rating || '0' }}</span>
                                    <span class="text-xs text-gray-400">({{ (parseInt(product.rating_count) || 0) + (product.testimonials_count || 0) }})</span>
                                </div>
                                <div class="flex items-center gap-1">
                                    <svg class="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z">
                                        </path>
                                    </svg>
                                    <span class="text-xs text-gray-500">{{ formatUsers(product.total_users) }}</span>
                                </div>
                                <span :class="[
                                    'px-2 py-0.5 rounded-md text-xs font-medium',
                                    getPlatformClass(product.platform)
                                ]">
                                    {{ formatPlatform(product.platform) }}
                                </span>
                            </div>
                        </div>
                    </router-link>
                </div>

                <!-- Empty State -->
                <div v-else class="text-center py-16">
                    <div class="w-24 h-24 bg-gray-100 rounded-full mx-auto mb-6 flex items-center justify-center">
                        <svg class="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
                                d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4">
                            </path>
                        </svg>
                    </div>
                    <h3 class="text-xl font-bold text-gray-900 mb-2">No products found</h3>
                    <p class="text-gray-600 mb-6">Try adjusting your search or filter to find what you're looking for.
                    </p>
                    <button @click="clearFilters" class="btn btn-primary">
                        Clear Filters
                    </button>
                </div>
            </div>
        </section>

        <AppFooter />
    </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useProductStore } from '@/stores/products'
import AppHeader from '@/components/layout/AppHeader.vue'
import AppFooter from '@/components/layout/AppFooter.vue'

const productStore = useProductStore()

const searchQuery = ref('')
const selectedCategory = ref(null)
const sortBy = ref('newest')

const loading = computed(() => productStore.loading)
const categories = computed(() => productStore.categories)
const products = computed(() => productStore.products)

const featuredProducts = computed(() => {
    return products.value.filter(p => p.featured).slice(0, 3)
})

const filteredProducts = computed(() => {
    let result = [...products.value]

    // Filter by category
    if (selectedCategory.value) {
        result = result.filter(p => p.category_id === selectedCategory.value)
    }

    // Filter by search
    if (searchQuery.value) {
        const query = searchQuery.value.toLowerCase()
        result = result.filter(p =>
            p.title?.toLowerCase().includes(query) ||
            p.short_description?.toLowerCase().includes(query) ||
            p.developer_name?.toLowerCase().includes(query)
        )
    }

    // Sort
    switch (sortBy.value) {
        case 'popular':
            result.sort((a, b) => (b.total_users || 0) - (a.total_users || 0))
            break
        case 'rating':
            result.sort((a, b) => (b.rating || 0) - (a.rating || 0))
            break
        case 'price_low':
            result.sort((a, b) => (a.price || 0) - (b.price || 0))
            break
        case 'price_high':
            result.sort((a, b) => (b.price || 0) - (a.price || 0))
            break
        default:
            result.sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }

    return result
})

onMounted(async () => {
    await Promise.all([
        productStore.fetchProducts(),
        productStore.fetchCategories()
    ])
})

const handleSearch = () => {
    // Debounce is handled by Vue's reactivity
}

const clearFilters = () => {
    searchQuery.value = ''
    selectedCategory.value = null
    sortBy.value = 'newest'
}

const formatUsers = (count) => {
    if (!count) return '0'
    if (count >= 1000000) return (count / 1000000).toFixed(1) + 'M'
    if (count >= 1000) return (count / 1000).toFixed(1) + 'K'
    return count.toString()
}

const formatPlatform = (platform) => {
    const platforms = {
        'mobile': 'Mobile',
        'web': 'Web',
        'desktop': 'Desktop',
        'cross-platform': 'Cross-Platform'
    }
    return platforms[platform] || platform
}

const getPlatformClass = (platform) => {
    const classes = {
        'mobile': 'bg-blue-100 text-blue-700',
        'web': 'bg-green-100 text-green-700',
        'desktop': 'bg-purple-100 text-purple-700',
        'cross-platform': 'bg-orange-100 text-orange-700'
    }
    return classes[platform] || 'bg-gray-100 text-gray-700'
}
</script>

<style scoped>
.scrollbar-hide::-webkit-scrollbar {
    display: none;
}

.scrollbar-hide {
    -ms-overflow-style: none;
    scrollbar-width: none;
}

.line-clamp-2 {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}
</style>
